import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Handshake, ShieldCheck, Container } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/motion";
import usePageMeta from "@/hooks/usePageMeta";
import { BRAND, IMAGES } from "@/data/content";

// Edit this list anytime — each sector opens its product image when clicked
const SECTORS = [
  {
    name: "Agro, Pulses & Indian Spices",
    image: "/assets/sector-agro-spices.jpg",
    blurb: "Makai, juar, bajra, green mug, tuver dal and other dals — plus red chilli, haldi, jira, variyali, dhaniya and kali mirch. 100% natural, hygienically processed and packed for global markets.",
  },
  {
    name: "Apparel & Fashion Wear",
    image: "/assets/sector-apparel-fashion.jpg",
    blurb: "Export-quality shirts, blazers, t-shirts, women's western wear and Indian dresses — premium quality, stylish designs, made to global standards.",
  },
  {
    name: "Engg., Electrical & Auto Parts",
    image: "/assets/sector-engg-auto-parts.jpg",
    blurb: "Gears & gear boxes, two/four-wheeler aluminium metal parts, electrical relays & breakers, and MCB & switch parts — precision engineered for reliable performance.",
  },
  {
    name: "Handicrafts & Décor Products",
    image: "/assets/sector-handicrafts-decor.jpg",
    blurb: "Marble statues & décor, painted pottery, Kachhi handwork items and wooden handcarvings — handmade elegance, rooted in tradition, crafted for the world.",
  },
  {
    name: "Kitchen Ware & Small Appliances",
    image: "/assets/sector-kitchen-ware.jpg",
    blurb: "Non-stick kitchenware, copper bottles, small atta chakki and hand-operating kitchen appliances — smart kitchen, better living, global quality.",
  },
  {
    name: "Leather Goods & Other Products",
    image: "/assets/sector-leather-goods.jpg",
    blurb: "Wallets, handbags for men & women, belts for men and women, and jackets for men & women — premium quality leather, expert craftsmanship and fine finish.",
  },
];
const MARKETS = ["[Middle East]", "[European Union]", "[North America]", "[South-East Asia]", "[Africa]", "[Australia]"];

const WHY = [
  {
    icon: ShieldCheck,
    title: "Reliability, Proven Over Decades",
    desc: "Four decades of shipments delivered, commitments honoured and disputes resolved fairly. My word is my working capital.",
  },
  {
    icon: Globe2,
    title: "A Network Built Over 40 Years",
    desc: "Buyers, suppliers, freight forwarders, customs agents and trade bodies — relationships that took a lifetime to build, working for you.",
  },
  {
    icon: Handshake,
    title: "Straight Dealing, Always",
    desc: "Transparent pricing, honest timelines and clear communication at every stage. No surprises hidden in the fine print.",
  },
];

export default function EximBusiness() {
  usePageMeta(
    `Export-Import Business — ${BRAND.name}`,
    "Direct export-import trading across global markets. Sourcing, logistics, documentation and delivery handled end-to-end by a 40-year trade veteran."
  );

  const [activeSector, setActiveSector] = useState(null);

  return (
    <div data-testid="exim-business-page">
      <PageHero
        kicker="Pillar 01 — Trade"
        lines={["Export-Import", "Business."]}
        description="Direct trading operations built on four decades of global relationships. From sourcing and negotiation to logistics, documentation and final delivery — handled end-to-end, the old-school reliable way."
        accent="gold"
        image={IMAGES.hero}
      />

      {/* WHAT WE DO */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-12 md:grid-cols-12">
            <Reveal className="md:col-span-5">
              <p className="kicker mb-5 text-gold">What We Trade</p>
              <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
                Products & Sectors <span className="italic text-gold">We Handle.</span>
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-slate-400 md:text-base">
                Decades of hands-on trading across multiple product categories — with the sourcing
                networks, quality checks and logistics know-how to move goods reliably across borders.
              </p>
            </Reveal>
            <div className="md:col-span-7">
              <div className="flex flex-wrap gap-3">
                {SECTORS.map((s, i) => (
                  <Reveal key={s.name} delay={i * 0.06}>
                    <button
                      type="button"
                      data-testid={`sector-chip-${i}`}
                      onClick={() => setActiveSector(s)}
                      className="inline-block rounded-full border border-gold/30 bg-gold/5 px-5 py-2.5 text-sm text-slate-200 transition-[background-color,border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold hover:bg-gold/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                    >
                      {s.name}
                    </button>
                  </Reveal>
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-500">Tap a category to view the products we handle.</p>
              <Reveal delay={0.2} className="mt-12">
                <div className="rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-8">
                  <div className="flex items-center gap-3">
                    <Container className="h-5 w-5 text-gold" strokeWidth={1.5} />
                    <h3 className="font-serif text-xl text-white">Markets Served</h3>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
                    {MARKETS.map((m, i) => (
                      <span key={i} data-testid={`market-item-${i}`} className="flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-gold/60" /> {m}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="kicker mb-5 text-gold">Why Choose Us</p>
            <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
              Trade With Someone Who Has <span className="italic text-gold">Seen It All.</span>
            </h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.12} className="h-full">
                <div className="h-full rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-8 transition-[transform,border-color] duration-500 hover:-translate-y-2 hover:border-gold/50">
                  <w.icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-xl text-white">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14 text-center">
            <Link to="/contact" data-testid="exim-discuss-button" className="btn-primary">
              Discuss a Trade Opportunity <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTOR PRODUCT MODAL */}
      <Dialog open={!!activeSector} onOpenChange={(open) => !open && setActiveSector(null)}>
        <DialogContent
          data-testid="sector-modal"
          className="max-h-[92vh] max-w-2xl overflow-y-auto border-[#1E3A5F] bg-[#0A2540] p-0 text-white"
        >
          {activeSector && (
            <div>
              <div className="w-full overflow-hidden border-b border-[#1E3A5F]">
                <img
                  data-testid="sector-modal-image"
                  src={activeSector.image}
                  alt={`${activeSector.name} products`}
                  className="mx-auto block h-auto max-h-[52vh] w-full object-contain"
                />
              </div>
              <div className="p-8 pt-5">
                <p className="kicker mb-3 text-gold">Products We Trade</p>
                <DialogTitle data-testid="sector-modal-title" className="font-serif text-2xl text-white md:text-3xl">
                  {activeSector.name}
                </DialogTitle>
                <DialogDescription data-testid="sector-modal-blurb" className="mt-3 text-sm leading-relaxed text-slate-400 md:text-base">
                  {activeSector.blurb}
                </DialogDescription>
                <Link
                  to="/contact"
                  data-testid="sector-modal-cta"
                  className="btn-primary mt-6"
                  onClick={() => setActiveSector(null)}
                >
                  Enquire About {activeSector.name} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CTASection
        kicker="Trade With Confidence"
        title="Have a product to move, or a market to reach?"
        subtitle="Let's discuss the opportunity — sourcing, pricing, logistics and everything in between."
        ctaLabel="Discuss a Trade Opportunity"
      />
    </div>
  );
}
